import { userOrdersPath } from "../../functions/src/admin/paths";
import { getWalletHistorySchoolYearOrderDateBounds, toDateFromServer } from "../../functions/src/api/dates";
import type { Order } from "../../functions/src/types";
import type { DataSnapshot } from "./firebase";
import firebase, { auth } from "./firebase";
import { useDatabase } from "./firebaseHooks";
import { useSchoolYearHistorySetup, type SchoolYearHistorySetup } from "./schoolYearHistorySetup";
import { useCallback, useMemo } from "react";

export const ORDER_DATE_KEY = /^\d{4}_\d{2}_\d{2}$/;

export type OrderHistoryDayMeta = {
    date: string;
    price: number;
    timestamp?: number;
    checkoutCreatedAt?: number;
};

export type OrderHistoryGroup = {
    id: string;
    placedAt: number;
    dates: string[];
    totalPrice: number;
};

function orderHasHistoryContent(order: Order): boolean {
    if (typeof order.price === "number" && order.price > 0) return true;
    const hasMeals = Object.values(order.meals ?? {}).some((m) => (m.quantity ?? 0) > 0);
    const hasSnacks = Object.values(order.snacks ?? {}).some((s) => (s.quantity ?? 0) > 0);
    return hasMeals || hasSnacks;
}

export function orderHistoryDayMetaFromValue(date: string, order: Order | null | undefined): OrderHistoryDayMeta | null {
    if (!ORDER_DATE_KEY.test(date) || !order) return null;
    const checkoutCreatedAt = typeof order.checkoutCreatedAt === "number" ? order.checkoutCreatedAt : undefined;
    const timestamp = typeof order.timestamp === "number" ? order.timestamp : undefined;
    if (checkoutCreatedAt == null && timestamp == null && !orderHasHistoryContent(order)) return null;
    return {
        date,
        price: typeof order.price === "number" ? order.price : 0,
        timestamp,
        checkoutCreatedAt,
    };
}

function makeOrderHistoryGroup(id: string, placedAt: number, days: OrderHistoryDayMeta[]): OrderHistoryGroup {
    const dates = days.map((d) => d.date).sort((a, b) => b.localeCompare(a));
    const totalPrice = days.reduce((sum, d) => sum + (d.price || 0), 0);
    const latestActivity = Math.max(placedAt, ...days.map((d) => d.timestamp ?? 0));
    return { id, placedAt: latestActivity || placedAt, dates, totalPrice };
}

/** One group per checkout (shared checkoutCreatedAt); legacy single-day orders are one group each. */
export function groupOrderHistoryDays(days: OrderHistoryDayMeta[]): OrderHistoryGroup[] {
    const groups: OrderHistoryGroup[] = [];
    const byCheckout = new Map<number, OrderHistoryDayMeta[]>();

    for (const day of days) {
        if (typeof day.checkoutCreatedAt === "number") {
            const list = byCheckout.get(day.checkoutCreatedAt) ?? [];
            list.push(day);
            byCheckout.set(day.checkoutCreatedAt, list);
        } else {
            const placedAt = typeof day.timestamp === "number" ? day.timestamp : 0;
            groups.push(makeOrderHistoryGroup(`date:${day.date}`, placedAt, [day]));
        }
    }

    for (const [createdAt, list] of byCheckout) {
        groups.push(makeOrderHistoryGroup(`checkout:${createdAt}`, createdAt, list));
    }

    groups.sort((a, b) => {
        if (b.placedAt !== a.placedAt) return b.placedAt - a.placedAt;
        return (b.dates[0] ?? "").localeCompare(a.dates[0] ?? "");
    });
    return groups;
}

export function useUserOrdersForSchoolYear(schoolYearStartYear: number | null): DataSnapshot | undefined {
    const uid = auth.currentUser?.uid;
    const { startKey, endKey } = useMemo(
        () => (schoolYearStartYear != null ? getWalletHistorySchoolYearOrderDateBounds(schoolYearStartYear) : { startKey: "", endKey: "" }),
        [schoolYearStartYear]
    );
    const filter = useCallback(
        (ref: firebase.database.Reference) => ref.orderByKey().startAt(startKey).endAt(endKey),
        [startKey, endKey]
    );
    return useDatabase(uid && schoolYearStartYear != null ? userOrdersPath(uid) : null, filter, true);
}

function useOrderHistoryLatestSnapshot(): DataSnapshot | undefined | null {
    const uid = auth.currentUser?.uid;
    const filter = useCallback(
        (ref: firebase.database.Reference) => ref.orderByKey().limitToLast(40),
        []
    );
    return useDatabase(uid ? userOrdersPath(uid) : null, filter, true);
}

function useOrderHistoryLatestDateKey(): string | null | undefined {
    const uid = auth.currentUser?.uid;
    const latestSnapshot = useOrderHistoryLatestSnapshot();

    return useMemo((): string | null | undefined => {
        if (!uid) return undefined;
        if (latestSnapshot === undefined || latestSnapshot === null) return undefined;
        if (!latestSnapshot.exists()) return null;
        let latest = "";
        latestSnapshot.forEach((child) => {
            const key = child.key!;
            if (ORDER_DATE_KEY.test(key) && key > latest) latest = key;
        });
        return latest || null;
    }, [uid, latestSnapshot]);
}

/** Registration → current year range; default = school year of most recent order day. */
export function useOrderHistoryYearSetup(): SchoolYearHistorySetup {
    const lastDateKey = useOrderHistoryLatestDateKey();
    const lastActivityTimestamp = useMemo((): number | null | undefined => {
        if (lastDateKey === undefined) return undefined;
        if (lastDateKey === null) return null;
        try {
            return toDateFromServer(lastDateKey).getTime();
        } catch {
            return null;
        }
    }, [lastDateKey]);

    return useSchoolYearHistorySetup(lastActivityTimestamp);
}
